import { useEffect } from 'react';
import { useStudioMediaMode } from '../studio/use-studio-media-mode';
import { useHeldMode } from '../studio/use-held-mode';
import { HeldModeBody } from '../studio/HeldModeBody';
import { api } from '../../api';
import type { UserAvatar } from '../../api/people';
import { uiText } from '../../i18n';
import { studioApi, studioCheapOffer, studioExpectedPrice, studioFields, studioUnitPrice, type StudioApp, type StudioForm } from '../studio/studio-api';
import { useStudioDocument } from '../studio/use-studio-document';
import { useStudioForm } from '../studio/use-studio-form';
import { record, rows, text } from '../studio/studio-content';
import { StudioMediaHeader } from '../studio/StudioMediaHeader';
import { PhotoFieldInput } from './PhotoFieldInput';
import { PhotoOrderResult } from './PhotoOrderResult';
import { PhotoPeople } from './PhotoPeople';
import { PhotoStyles } from './PhotoStyles';
import { PhotoShooting } from './PhotoShooting';
import {
  photoChangeChips,
  photoDescriptionOnForm,
  photoMissing,
  photoPlaceholders,
  photoPolicy,
  photoThemeOrder,
  photoTweakChips,
  photoUploadHint,
  photoValues,
  photoLabels,
  type PhotoPolicy,
} from './photo-model';
import { StudioSettings, StudioCreateButton, StudioSettingsSection, type StudioSettingSlot } from '../studio/StudioFormParts';
import { shootCells, useShootDicts } from '../studio/use-studio-shoot';
import { uploadExample } from '../studio/StudioUploadExample';
import '../studio/native-template.css';
import './native-photo-layout.css';
interface Props {
  app: StudioApp;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
  headerActive?: boolean;
}
/** Пояснения строк «Формата» (§2.2) */
const formatNotes: Record<string, string> = {
  auto: 'Без обрезки, как снято',
  '1:1': 'Для аватарки и ленты',
  '3:4': 'Как снимок с телефона',
  '4:5': 'Для ленты',
  '9:16': 'На весь экран телефона',
  '16:9': 'Для обложки и экрана',
};
const OWN_KEYS = ['photo', 'description', 'style', 'reference', 'shoot', 'crop_x', 'crop_y', 'zoom', 'format', 'count'];
export function PhotoOrderForm(props: Props) {
  const initial = props.initialInputs
    ? {
        ...props.initialInputs,
        ...(typeof props.initialInputs.text === 'string' && props.initialInputs.description === undefined
          ? { description: props.initialInputs.text }
          : {}),
        ...(typeof props.initialInputs.sty === 'string' && props.initialInputs.style === undefined
          ? { style: props.initialInputs.sty }
          : {}),
      }
    : undefined;
  const { active, seed, switching, error, switchMode } = useStudioMediaMode(props.app, initial, 'photo');
  const held = useHeldMode(active, seed);
  const screen = useStudioDocument(signal => api.request<Record<string, unknown>>('/app/studio/screens/photo', { signal }), 'photo-tools');
  const tools = rows(record(screen.data?.photo).tools).filter(
    tool => Number.isSafeInteger(tool.app_id) && Number(tool.app_id) > 0 && text(tool.title),
  );
  const themeOrder = photoThemeOrder(screen.data);
  return (
    <section className="server-photo native-studio-form">
      {screen.error && (
        <p role="alert">
          {uiText('Не удалось загрузить режимы фото.')}{' '}
          <button type="button" onClick={screen.reload}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {error && <p role="alert">{uiText(error)}</p>}
      {held.bodies.map(body => (
        <HeldModeBody key={body.app.id} body={body} lock={switching || held.holding}>
          <PhotoFormBody
            app={body.app}
            avatar={props.avatar}
            initialInputs={body.seed}
            headerActive={props.headerActive && body.visible}
            tools={tools}
            themeOrder={themeOrder}
            switching={false}
            onSwitch={switchMode}
            onReady={held.ready}
          />
        </HeldModeBody>
      ))}
    </section>
  );
}
function PhotoFormBody({
  app,
  avatar,
  initialInputs,
  tools,
  themeOrder,
  switching,
  onSwitch,
  headerActive,
  onReady,
}: Props & {
  tools: Record<string, unknown>[];
  /** Порядок тем «Всех стилей» из документа экрана (photo.themes) */
  themeOrder?: string[];
  switching: boolean;
  onSwitch(id: number, inputs: Record<string, string>, owner?: number, transfer?: boolean): void;
  /** Форма режима пришла (или не пришла): тело можно показать вместо прежнего */
  onReady?(appId: number): void;
}) {
  const baseState = useStudioForm({ app, avatar, initialInputs });
  const formReady = !!baseState.form || !!baseState.error;
  useEffect(() => {
    if (formReady) onReady?.(app.id);
  }, [formReady, onReady, app.id]);
  const state = switching ? { ...baseState, locked: true } : baseState;
  const { form, inputs, intent, orderId, busy, locked, submissionBlocked, rights, error } = state;
  const policy = form ? photoPolicy(form) : null;
  const missing = form ? photoMissing(form, inputs, rights, state.uploadIds ?? {}) : null;
  const fields = studioFields(form);
  const field = (key: string) => fields.find(item => item.key === key);
  const description = field('description');
  const onForm = !!policy && photoDescriptionOnForm(policy, inputs);
  const shared = ['generate', 'style', 'edit'].includes(policy?.mode ?? '');
  const tool = (mode: string) => tools.find(item => item.mode === mode);
  /* «Без стиля» у «Из фото» — режим «Изменить фото» (edit): лента стилей берётся у режима «В новом стиле» (style). */
  const styleTool = tool('style');
  const styleCatalog = useStudioDocument(
    signal => (policy?.mode === 'edit' && styleTool ? studioApi.form(Number(styleTool.app_id), signal) : Promise.resolve(null)),
    `photo-style-catalog:${policy?.mode === 'edit' ? Number(styleTool?.app_id ?? 0) : 0}`,
  );
  const fallbackPolicy = styleCatalog.data ? photoPolicy(styleCatalog.data as StudioForm) : null;
  const rail: PhotoPolicy | null = policy?.styles.length ? policy : (fallbackPolicy ?? null);
  const shoot = useShootDicts(!!record(form).shoot || !!record(record(form).settings).shoot);
  const format = field('format'),
    count = field('count');
  const price = state.price ?? (form ? studioExpectedPrice(form, inputs) : 0);
  const many = Number(inputs.count || count?.default_value || 1);
  const tweak = !onForm && description;
  const extra = fields.filter(
    item =>
      !OWN_KEYS.includes(item.key) &&
      !/^cast_|^action_/.test(item.key) &&
      !(item.values.length === 1 && item.required) &&
      !item.upload &&
      // В карточке только ячейки спецификации (§2.2): служебные поля сервера (mode, preset, output_type) идут умолчанием.
      !!photoLabels[item.key] &&
      item.key !== 'output_type',
  );
  const descriptionLabel = policy?.mode === 'generate' ? 'Описание' : onForm ? 'Что поменять' : 'Что изменить';
  const slots: StudioSettingSlot[] = [
    ...(format && format.values.length > 1
      ? [{ title: 'Формат', value: photoValues[inputs.format] || inputs.format || 'Как у фото', section: 'format', kind: 'choice' as const }]
      : []),
    ...(count && count.values.length > 1
      ? [{ title: 'Сколько фото', value: `${inputs.count || count.values[0]} фото`, section: 'count', kind: 'choice' as const }]
      : []),
    ...(policy && !['restore', 'colorize'].includes(policy.mode) ? shootCells(form, inputs, shoot.data) : []),
    ...extra
      .filter(item => item.values.length)
      .map(item => ({
        title: photoLabels[item.key],
        value: photoValues[inputs[item.key]] ?? (inputs[item.key] || uiText('По умолчанию')),
        section: item.key,
      })),
    ...(tweak
      ? [
          {
            title: 'Что изменить',
            value: (inputs.description ?? '').trim().replace(/\s+/g, ' '),
            empty: !(inputs.description ?? '').trim(),
            kind: 'text' as const,
            section: 'description',
            wide: Array.from((inputs.description ?? '').trim()).length > 18,
          },
        ]
      : []),
  ];
  const styleSelect =
    policy && shared
      ? (id: string) => {
          // «Без стиля» и стиль — разные режимы сервера: переносим заполненное, как смену сегмента.
          if (policy.mode === 'style' && id === 'none' && tool('edit')) onSwitch(Number(tool('edit')!.app_id), { ...inputs, style: 'none' }, state.ownerId, true);
          else if (policy.mode === 'edit' && id !== 'none' && styleTool)
            onSwitch(Number(styleTool.app_id), { ...inputs, style: id }, state.ownerId, true);
          else
            state.setInputs(current => {
              const next: Record<string, string> = { ...current, style: id };
              delete next.reference;
              return next;
            });
        }
      : undefined;
  return (
    <>
      <StudioMediaHeader
        kind="photo"
        title={policy && shared ? 'Фото' : app.name}
        price={studioUnitPrice(form) ?? app.price ?? form?.expected_price}
        mode={policy?.mode === 'generate' ? 'text' : 'file'}
        enabled={headerActive}
        showModes={!orderId && !!policy && shared}
        locked={locked || switching || !state.ownerId}
        fileAvailable={tools.some(item => item.mode === 'style' || item.mode === 'edit')}
        textAvailable={tools.some(item => item.mode === 'generate')}
        onMode={mode => {
          const next = mode === 'text' ? tool('generate') : (styleTool ?? tool('edit'));
          if (next) onSwitch(Number(next.app_id), inputs, state.ownerId);
        }}
      />

      {!form && !error && <p role="status">{uiText('Загружаем…')}</p>}
      {error && (
        <p role="alert">
          {uiText(error)}{' '}
          {!intent && (
            <button type="button" onClick={() => state.setRevision(value => value + 1)}>
              {uiText('Обновить')}
            </button>
          )}
        </p>
      )}
      {form && !orderId && (
        <form
          onSubmit={event => {
            event.preventDefault();
            if (!busy && !switching && !submissionBlocked && (intent || (!missing && form.available))) void state.submit();
          }}
        >
          {fields
            .filter(item => item.key === 'photo')
            .map(item => (
              <PhotoFieldInput
                key={item.key}
                field={item}
                state={state}
                avatar={avatar}
                frame={{
                  subtitle: policy ? photoUploadHint(policy) : undefined,
                  example: policy && ['restore', 'colorize'].includes(policy.mode) ? uploadExample(policy.example) : null,
                }}
              />
            ))}
          {/* Поле на форме — box шаблона: чип ставит свой полный текст, повторное нажатие очищает (bot-photo.js, data-pf-chip) */}
          {description && onForm && (
            <PhotoFieldInput
              field={description}
              state={state}
              avatar={avatar}
              label={descriptionLabel}
              presets={policy?.mode === 'generate' ? undefined : photoChangeChips}
              chipMode="set"
              placeholder={
                policy?.mode === 'generate'
                  ? photoPlaceholders.tx
                  : policy?.mode === 'edit' && !inputs.reference
                    ? photoPlaceholders.edit
                    : photoPlaceholders.ph
              }
            />
          )}
          {policy && rail && (fields.some(item => item.key === 'style') || policy.mode === 'edit') && (
            <PhotoStyles
              state={state}
              policy={rail}
              allowNone={shared}
              selectedId={policy.mode === 'edit' ? 'none' : undefined}
              onSelectStyle={styleSelect}
              unitPrice={studioUnitPrice(form) ?? app.price ?? form.expected_price}
              themeOrder={themeOrder}
            />
          )}
          {policy && <PhotoPeople state={state} policy={policy} avatar={avatar} />}
          {!!slots.length && (
            <StudioSettings disabled={locked} slots={slots}>
              {format && format.values.length > 1 && (
                <StudioSettingsSection id="format">
                  <PhotoFieldInput field={format} state={state} avatar={avatar} descriptions={formatNotes} />
                </StudioSettingsSection>
              )}
              {count && count.values.length > 1 && (
                <StudioSettingsSection id="count">
                  <PhotoFieldInput field={count} state={state} avatar={avatar} />
                </StudioSettingsSection>
              )}
              {policy && !['restore', 'colorize'].includes(policy.mode) && <PhotoShooting state={state} />}
              {extra.map(item => (
                <StudioSettingsSection key={item.key} id={item.key}>
                  <PhotoFieldInput field={item} state={state} avatar={avatar} />
                </StudioSettingsSection>
              ))}
              {tweak && (
                <StudioSettingsSection id="description" kind="text">
                  <PhotoFieldInput
                    field={description}
                    state={state}
                    avatar={avatar}
                    label="Что изменить"
                    hint="необязательно"
                    presets={photoTweakChips}
                    chipMode="add"
                    placeholder={photoPlaceholders.ph}
                  />
                </StudioSettingsSection>
              )}
            </StudioSettings>
          )}
          {!form.available && <p role="status">{uiText('Создание сейчас недоступно')}</p>}
          <StudioCreateButton
            kind="photo"
            price={price}
            label={many > 1 ? `Создать ${many} фото` : 'Создать фото'}
            busy={busy || switching}
            uploading={state.uploadingFile}
            blocked={submissionBlocked}
            pending={!!intent}
            missing={missing}
            available={form.available}
            balance={state.balance}
            cheap={studioCheapOffer(form)}
            onCheap={() => void state.submit({ cheap: true })}
            onCreate={() => void state.submit()}
          />
        </form>
      )}
      {!form && intent && !orderId && (
        <button disabled={busy || submissionBlocked} onClick={() => void state.submit()}>
          {uiText('Продолжить проверку')}
        </button>
      )}
      <PhotoOrderResult state={state} />
    </>
  );
}
